<template>
  <div id="Invoice-create" class="group-setup">
    <v-progress-linear
      indeterminate
      color="blue darken-2"
      v-if="loading"
    ></v-progress-linear>
    <v-layout row v-if="errors">
      <v-flex>
        <Alert @dismissed="onDismissed" :text="errors"></Alert>
      </v-flex>
    </v-layout>
    <v-form ref="InvoiceForm" v-model="valid" v-if="!loading">
      <v-row>
        <v-col cols="12">
          <v-card class="pa-5" flat>
            <HeaderDividerBlock
              :headerText="'Create Invoice'"
            ></HeaderDividerBlock>
            <v-row>
              <v-col cols="6" md="4" sm="6">
                <v-col class="pa-0 ma-0">
                  <v-col class="pa-0 ma-0">
                    <p class="text-title">Customer</p>
                  </v-col>
                  <v-autocomplete
                    v-model="formData.customer_id"
                    :items="customers"
                    item-text="company_name"
                    item-value="id"
                    placeholder="Select Customer"
                    outlined
                    dense
                    clearable
                    :rules="[rules.required]"
                  ></v-autocomplete>
                </v-col>
              </v-col>

              <!-- Branch -->
              <v-col cols="6" md="4" sm="6">
                <v-col class="pa-0 ma-0">
                  <v-col class="pa-0 ma-0">
                    <p class="text-title">Branch</p>
                  </v-col>
                  <v-autocomplete
                    v-model="formData.branch_id"
                    :items="branches"
                    item-text="name"
                    item-value="id"
                    placeholder="Select Branch"
                    outlined
                    dense
                    clearable
                    :rules="[rules.required]"
                  ></v-autocomplete>
                </v-col>
              </v-col>

              <v-col cols="6" md="4" sm="6">
                <v-col class="pa-0 ma-0">
                  <v-col class="pa-0 ma-0">
                    <p class="text-title">Store</p>
                  </v-col>
                  <v-autocomplete
                    v-model="formData.store_id"
                    :items="stores"
                    item-text="store_name"
                    item-value="id"
                    placeholder="Select Store"
                    outlined
                    dense
                    clearable
                    :disabled="!formData.branch_id"
                    :rules="[rules.required]"
                  ></v-autocomplete>
                </v-col>
              </v-col>

              <!-- Invoice Date -->
              <v-col cols="6" md="4" sm="6">
                <v-col class="pa-0 ma-0">
                  <v-col class="pa-0 ma-0">
                    <p class="text-title">Invoice Date</p>
                  </v-col>
                  <DatePicker
                    v-model="formData.invoice_date"
                    placeholder="Select Invoice Date"
                  />
                </v-col>
              </v-col>

              <!-- Due Date -->
              <v-col cols="6" md="4" sm="6">
                <v-col class="pa-0 ma-0">
                  <v-col class="pa-0 ma-0">
                    <p class="text-title">Select Due Date</p>
                  </v-col>
                  <DatePicker
                    v-model="formData.due_date"
                    placeholder="Select Due Date"
                    :min="datePicker.min"
                  />
                </v-col>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="4" sm="6">
                <v-radio-group v-model="priceType" row>
                  <v-radio label="Retail" value="retail"></v-radio>
                  <v-radio label="Wholesale" value="wholesale"></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>
            <!-- Items Table -->
            <v-col cols="12">
              <ItemsTable
                ref="itemsTable"
                :items="formData.items"
                :products="products"
                :branch_id="formData.branch_id"
                :store_id="formData.store_id"
                :price-type="priceType"
                @add-item="addItem"
                @remove-item="removeItem"
              />
            </v-col>

            <!-- Summary -->
            <v-row cols="12" v-if="formData.items.length > 0">
              <v-col></v-col>
              <v-col cols="12" md="4" sm="12">
                <v-col class="ma-0 pa-0">
                  <p class="text-h5 font-weight-bold">Summary</p>
                </v-col>
                <v-col class="px-7 pa-10 total-section">
                  <v-row class="justify-space-between">
                    <p class="text-subtitle-1 font-weight-bold text-uppercase">
                      Sub Total
                    </p>
                    <p>{{ formatCurrency(calculateSubtotal()) }}</p>
                  </v-row>
                  <v-row class="justify-space-between">
                    <p class="text-subtitle-1 font-weight-bold text-uppercase">
                      Total Discount
                    </p>
                    <p>{{ formatCurrency(calculateTotalDiscount()) }}</p>
                  </v-row>
                  <v-row class="justify-space-between">
                    <p class="text-subtitle-1 font-weight-bold text-uppercase">
                      Total Tax
                    </p>
                    <p>{{ formatCurrency(calculateTotalTax()) }}</p>
                  </v-row>
                  <v-row class="justify-space-between">
                    <p class="text-subtitle-1 font-weight-bold text-uppercase">
                      Grand Total
                    </p>
                    <p>{{ formatCurrency(calculateGrandTotal()) }}</p>
                  </v-row>
                </v-col>
              </v-col>
            </v-row>

            <!-- Comments -->
            <v-col cols="12">
              <v-col class="pa-0 ma-0">
                <p class="text-title">Comments</p>
              </v-col>
              <v-col>
                <v-textarea
                  v-model="formData.comment"
                  outlined
                  dense
                  auto-grow
                ></v-textarea>
              </v-col>
            </v-col>

            <!-- Default Store -->
            <v-col
              cols="6"
              sm="6"
              v-if="hasPermission('invoice.edit') && formData.items.length > 0"
            >
              <v-col class="pa-0 ma-0">
                <p class="text-title">Invoice Post</p>
              </v-col>
              <v-switch
                v-model="formData.is_post"
                inset
                :disabled="paymentData.pay"
              ></v-switch>
            </v-col>

            <v-col cols="6" sm="6" v-if="formData.items.length > 0">
              <v-checkbox
                v-model="paymentData.pay"
                label="Pay"
                color="primary"
              ></v-checkbox>
            </v-col>

            <v-col cols="12" v-if="paymentData.pay">
              <v-row>
                <v-col cols="12" md="4" sm="6">
                  <v-autocomplete
                    v-model="paymentData.payment_method_id"
                    :items="paymentOptions"
                    item-text="name"
                    item-value="id"
                    label="Payment Option"
                    outlined
                    dense
                    clearable
                    :rules="[rules.required]"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="4" sm="6">
                  <v-text-field
                    v-model="paymentData.paymentAmount"
                    label="Amount"
                    type="number"
                    outlined
                    dense
                    :rules="[
                      rules.required,
                      (value) =>
                        Number(value) <= calculateGrandTotal() ||
                        `Amount cannot exceed ${calculateGrandTotal()}`,
                    ]"
                    @input="onPaymentAmountInput($event)"
                    @change="onPaymentAmountInput($event)"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4" sm="12">
                  <v-text-field
                    v-model="paymentData.paymentRemark"
                    label="Remark"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-col>

            <!-- Actions -->
            <v-col cols="12" md="12" sm="12">
              <v-card-actions class="mt-10">
                <v-row>
                  <v-col cols="12" md="10" xs="6"></v-col>
                  <v-col cols="12" md="2" xs="6">
                    <v-btn
                      v-if="hasPermission('invoice.create')"
                      large
                      color="#00B3FF"
                      dark
                      depressed
                      block
                      @click="submitForm"
                    >
                      Create Invoice
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-actions>
            </v-col>
          </v-card>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");
import ItemsTable from "@/components/invoices/InvoiceItems.vue";
import HeaderDividerBlock from "@/components/shared/HeaderDividerBlock";
import Alert from "@/components/shared/Alert";
import { InvoiceService } from "@/services/invoice-service.js";
import { checkError } from "@/config/errors.js";
import DatePicker from "@/components/shared/DatePicker";
import { StockTransferService } from "@/services/stockTransfer-service.js";

export default {
  name: "InvoiceCreateForm",
  components: { ItemsTable, HeaderDividerBlock, Alert, DatePicker },
  props: {
    proploading: { type: Boolean, required: true },
  },
  data() {
    return {
      formData: {
        customer_id: "",
        branch_id: "",
        items: [],
        remarks: "",
        invoice_date: new Date().toISOString().substr(0, 10),
        due_date: new Date().toISOString().substr(0, 10),
        is_post: false,
      },
      paymentData: {
        invoice_id: null,
        pay: false,
        payment_method_id: "",
        paymentRemark: "",
        paymentAmount: "",
      },
      priceType: "retail",
      loading: this.proploading,
      valid: false,
      datePicker: {
        menu: false,
        min: new Date().toISOString().substr(0, 10),
      },
      customers: [],
      branches: [],
      stores: [],
      products: [],
      paymentOptions: [],
      rules: {
        required: (value) => !!value || "This field is required.",
      },
      errors: null,
    };
  },

  computed: {
    ...mapGetters({
      profile: "profile",
      hasPermission: "hasPermission",
    }),
  },

  methods: {
    addItem() {
      this.formData.items.push({
        id: "",
        description: "",
        quantity: 0,
        discount: 0,
        unit_price: 0,
        tax_percent: 0,
        cost: 0,
      });
    },
    removeItem(index) {
      this.formData.items.splice(index, 1);
    },

    calculateSubtotal() {
      return this.formData.items.reduce((sum, item) => {
        const unitPrice = parseFloat(item.unit_price) || 0;
        const discountPercent = parseFloat(item.discount) || 0;
        const discountedPrice = unitPrice - (unitPrice * discountPercent) / 100;
        return sum + discountedPrice * (parseFloat(item.quantity) || 0);
      }, 0);
    },

    calculateTotalTax() {
      return this.formData.items.reduce((totalTax, item) => {
        const unitPrice = parseFloat(item.unit_price) || 0;
        const discountPercent = parseFloat(item.discount) || 0;
        const discountedPrice = unitPrice - (unitPrice * discountPercent) / 100;
        const taxPercent = parseFloat(item.tax_percent) || 0;
        const quantity = parseFloat(item.quantity) || 0;
        return totalTax + (discountedPrice * taxPercent * quantity) / 100;
      }, 0);
    },

    calculateGrandTotal() {
      const subtotal = this.calculateSubtotal();
      const totalTax = this.calculateTotalTax();
      return Math.max(0, subtotal + totalTax);
    },

    calculateTotalDiscount() {
      return this.formData.items.reduce((totalDiscount, item) => {
        const unitPrice = parseFloat(item.unit_price) || 0;
        const discountPercent = parseFloat(item.discount) || 0;
        const quantity = parseFloat(item.quantity) || 0;
        const discountAmount = (unitPrice * discountPercent) / 100;
        return totalDiscount + discountAmount * quantity;
      }, 0);
    },

    formatCurrency(value) {
      const num = parseFloat(value || 0);
      return isNaN(num) ? "0.00" : num.toFixed(2);
    },

    onPaymentAmountInput(value) {
      const max = this.calculateGrandTotal();
      if (!value || Number(value) < 0) {
        this.paymentData.paymentAmount = 0;
      } else if (Number(value) > max) {
        this.paymentData.paymentAmount = max;
      }
    },

    resetForm() {
      this.formData = {
        customer_id: "",
        branch_id: "",
        items: [],
        remarks: "",
        invoice_date: new Date().toISOString().substr(0, 10),
        due_date: new Date().toISOString().substr(0, 10),
      };
      this.paymentData = {
        pay: false,
        paymentOption: "",
        payment_method_id: "",
        paymentRemark: "",
        paymentAmount: "",
      };
      // this.$refs.poForm.reset();
    },

    async submitForm() {
      if (!this.$refs.InvoiceForm.validate()) return;
      this.loading = true;
      try {
        const invoiceResponse = await InvoiceService.createInvoice(
          this.formData
        );
        const invoiceId = invoiceResponse?.data?.id;
        const branchId = invoiceResponse?.data?.branch_id;
        const customerId = invoiceResponse?.data?.customer_id;
        if (this.paymentData.pay && invoiceId && branchId && customerId) {
          this.paymentData.invoice_id = invoiceId;
          this.paymentData.branch_id = branchId;
          this.paymentData.customer_id = customerId;

          await InvoiceService.createPayment(this.paymentData);
        } else {
          console.log(
            "Payment not created. Either 'Pay' not checked or missing invoice details."
          );
          this.successMsg = "Payment was not created. Please try later";
          this.snackbar = true;
        }
      } catch (error) {
        console.error(error);
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        this.$router.push({ name: "InvoiceList" });
        this.loading = false;
      }
    },

    async fetchBranchesCustomersProducts() {
      this.loading = true;

      try {
        const [
          branchesResponse,
          customersResponse,
          productsResponse,
          paymentOptions,
        ] = await Promise.all([
          InvoiceService.getBranches(),
          InvoiceService.getCustomers(),
          InvoiceService.getProducts(),
          InvoiceService.getPaymentMethods(),
        ]);

        this.branches = branchesResponse.data;
        this.customers = customersResponse.data;
        this.products = productsResponse.data;
        this.paymentOptions = paymentOptions.data;
      } catch (error) {
        console.error("Error fetching data:", error);
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        this.loading = false;
      }
    },

    async fetchStores(branchId) {
      try {
        const storesResponse = await StockTransferService.getStores(branchId);
        this.stores = storesResponse.data;
      } catch (error) {
        this.stores = [];
      }
    },

    onDismissed() {
      this.errors = null;
    },
  },

  async created() {
    this.fetchBranchesCustomersProducts();
  },

  watch: {
    "formData.branch_id": {
      immediate: true,
      handler(newBranchId) {
        if (newBranchId) {
          this.fetchStores(newBranchId);
        } else {
          this.stores = [];
        }
      },
    },
    proploading(newVal) {
      this.loading = newVal;
    },
    priceType() {
      // Update prices for all items when priceType changes
      this.formData.items.forEach((item, index) => {
        if (item.id && item.batch_id) {
          this.$refs.itemsTable.fetchpricetaxdiscount(
            item.id,
            item.batch_id,
            index
          );
        }
      });
    },
    "paymentData.pay"(newVal) {
      if (newVal) {
        this.formData.is_post = true;
      }
    },
  },
};
</script>
